import { useState } from "react";
import { Table, Tag } from "antd";
import { Building2 } from "lucide-react";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { usePensionList } from "../../../hooks/api/payslips/hooks";

const formatCurrency = (v: any) =>
  v != null ? `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : "—";

const formatDate = (v: any) => (v ? dayjs(v).format("DD MMM YYYY") : "—");

const enrolmentColor = (s: string) => {
  if (!s) return "default";
  const sl = s.toLowerCase();
  if (sl === "enrolled" || sl === "active" || sl === "opted-in" || sl === "opted_in") return "green";
  if (sl === "opted-out" || sl === "opted_out" || sl === "inactive") return "red";
  if (sl === "pending" || sl === "processing") return "orange";
  return "default";
};

const PensionPage = () => {
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: res, isLoading } = usePensionList({ page, limit });
  const records: any[] = (res?.data as any) ?? [];
  const total = Number(res?.extra?.total ?? 0);

  const columns: ColumnType<any>[] = [
    {
      title: "Worker",
      key: "worker",
      render: (_: any, r: any) =>
        r.worker_name || r.worker || r.name || r.employee_name || "—",
      width: 180,
    },
    {
      title: "Pension Scheme",
      key: "scheme",
      render: (_: any, r: any) =>
        r.scheme_name || r.pension_scheme || r.provider || r.provider_name || "—",
      width: 180,
    },
    {
      title: "Enrolment Status",
      key: "status",
      render: (_: any, r: any) => {
        const s = r.enrolment_status || r.status || r.pension_status;
        return s ? (
          <Tag color={enrolmentColor(s)}>{String(s).toUpperCase().replace(/_/g, " ")}</Tag>
        ) : (
          "—"
        );
      },
      width: 150,
    },
    {
      title: "Employee Contribution",
      key: "employee_contrib",
      render: (_: any, r: any) =>
        formatCurrency(r.employee_contribution ?? r.employee_contrib ?? r.worker_contribution),
      width: 160,
    },
    {
      title: "Employer Contribution",
      key: "employer_contrib",
      render: (_: any, r: any) =>
        formatCurrency(r.employer_contribution ?? r.employer_contrib ?? r.company_contribution),
      width: 160,
    },
    {
      title: "Total Contribution",
      key: "total",
      render: (_: any, r: any) =>
        formatCurrency(r.total_contribution ?? r.total_contributions ?? r.total),
      width: 140,
    },
    {
      title: "Period",
      key: "period",
      render: (_: any, r: any) =>
        r.pay_period || r.period || (r.period_start ? `${formatDate(r.period_start)} – ${formatDate(r.period_end)}` : "—"),
      width: 160,
    },
    {
      title: "Enrolment Date",
      key: "enrolled_at",
      render: (_: any, r: any) =>
        formatDate(r.enrolled_at ?? r.enrolment_date ?? r.start_date ?? r.created_at),
      width: 130,
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Building2 size={20} className="text-slate-500" />
            <h1 className="text-xl font-semibold text-slate-800">Pension Auto-Enrolment</h1>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">
            Workplace pension enrolment and contribution records
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={records}
          columns={columns}
          rowKey={(r) => r.id ?? r._id ?? Math.random()}
          loading={isLoading}
          locale={{
            emptyText: (
              <div className="py-12 text-center text-slate-400">
                <Building2 size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No pension records found</p>
                <p className="text-sm mt-1">
                  Pension auto-enrolment data will appear here once records are created.
                </p>
              </div>
            ),
          }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showTotal: (t) => `${t} records`,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
          }}
          scroll={{ x: 1080 }}
          size="small"
        />
      </div>
    </div>
  );
};

export default PensionPage;
